import { useAlert } from "@components/alert/withAlert";
import { getTableDrillDownLink } from "@components/drillDown/DrillDown.utils";
import { formatDateToDateString } from "@components/inputs/date/utils";
import SectionSwitch from "@components/splitWizard/SectionSwitch";
import { IColumn, IRow } from "@components/table";
import TableWithAutoSizedColumns from "@components/table/TableWithAutoSizedColumns";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    IPrEmploymentEntity,
    IPrTaxpayerDeclarationEntity,
    PrEmployeeEntity,
    PrEmploymentEntity,
    PrTaxpayerDeclarationEntity
} from "@odata/GeneratedEntityTypes";
import { PrTaxpayerDeclarationStatusCode } from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { ISectionProps } from "@pages/payroll/payrollYearOperations/PayrollYearOperation.utils";
import {
    AlertWrapper,
    ClickableStyled,
    DataGrid,
    DataGridCell,
    TableWrapper,
    Text
} from "@pages/payroll/payrollYearOperations/screens/PayrollYearOperationScreen.styles";
import { getThemeColorForDeclarationStatus } from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.def";
import { logger } from "@utils/log";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AppContext } from "../../../../contexts/appContext/AppContext.types";
import { ColoredText } from "../../../../global.style";
import { ROUTE_EMPLOYEES, ROUTE_TAXPAYER_DECLARATIONS } from "../../../../routes";
import { getUtcDayjs } from "../../../../types/Date";
import { getAlertFromError } from "../../../../views/formView/Form.utils";

const TaxPayerDeclaration: React.FC<ISectionProps> = ({
                                                          task,
                                                          year,
                                                          onCheck,
                                                          storage
                                                      }) => {
    const { t } = useTranslation(["PayrollYearOperation", getEnumNameSpaceName(EntityTypeName.PrTaxpayerDeclarationStatus)]);
    const { alert, setAlert } = useAlert({});
    const [rows, setRows] = React.useState<IRow[]>([]);
    const [employeesWithoutDeclaration, setEmployeesWithoutDeclaration] = React.useState<string[]>(["ASDF"]);
    const context = useContext(AppContext);

    const nextYear = year ? parseInt(year) + 1 : undefined; // https://solitea-cz.atlassian.net/browse/DEV-42570
    
    // load taxpayer declaration statuses count
    useEffect((): void => {
        try {
            const nextYearDayjs = getUtcDayjs().set("year", nextYear);
            const declarationsRes = storage.oData.getEntitySetWrapper(EntitySetName.PrTaxpayerDeclarations)
                    .query()
                .filter(`${PrTaxpayerDeclarationEntity.Year} eq ${nextYear}`)
                .groupBy(PrTaxpayerDeclarationEntity.StatusCode, createPath(PrTaxpayerDeclarationEntity.Employee, PrEmployeeEntity.NumberOurs))
                    .aggregate("$count as @odata.count")
                    .fetchData<IPrTaxpayerDeclarationEntity[]>();
            const employmentRes = storage.oData.getEntitySetWrapper(EntitySetName.PrEmployments).query()
                .filter(`${PrEmploymentEntity.DateStart} le ${formatDateToDateString(nextYearDayjs.endOf("year"))} AND (${PrEmploymentEntity.DateEnd} eq null OR ${PrEmploymentEntity.DateEnd} ge ${formatDateToDateString(nextYearDayjs.startOf("year"))})`)
                .expand(PrEmploymentEntity.Employee, query => query.select(PrEmployeeEntity.NumberOurs))
                .fetchData<IPrEmploymentEntity[]>();


            Promise.all([declarationsRes, employmentRes]).then(([res1, res2]) => {
                const declaration = res1?.value ?? [];
                const employments = res2?.value ?? [];
                const employeesWithDeclaration = declaration.map((item) => item.Employee?.NumberOurs);
                const employeesWithoutDeclaration = new Set<string>(employments.filter((item) => !employeesWithDeclaration.includes(item.Employee?.NumberOurs)).map((item) => item.Employee?.NumberOurs));
                setEmployeesWithoutDeclaration(Array.from(employeesWithoutDeclaration));

                const statusCountMap = new Map<PrTaxpayerDeclarationStatusCode, number>();
                declaration.forEach((item) => {
                    const count = statusCountMap.get(item.StatusCode) ?? 0;
                    statusCountMap.set(item.StatusCode, count + (item._metadata[""].count ?? 0));
                });

                const rows = Array.from(statusCountMap.entries()).map(([statusCode, count]) => {
                    const name = getEnumDisplayValue(EntityTypeName.PrTaxpayerDeclarationStatus, statusCode);
                    return {
                        id: statusCode.toString(),
                        values: {
                            [PrTaxpayerDeclarationEntity.StatusCode]: {
                                tooltip: name,
                                value: <ColoredText
                                    color={getThemeColorForDeclarationStatus(statusCode)}>
                                    {name}
                                </ColoredText>
                            },
                            count: getTableDrillDownLink(count.toString(), {
                                route: ROUTE_TAXPAYER_DECLARATIONS,
                                context: context,
                                filters: {
                                    [PrTaxpayerDeclarationEntity.Status]: statusCode,
                                    [PrTaxpayerDeclarationEntity.Year]: nextYear
                                }
                            })
                        }
                    };
                });

                setRows(rows);
            });
        } catch (e) {
            setAlert(getAlertFromError(e));
            logger.error("Error while fetching Taxpayer Declarations", e);
        }

    }, [year, storage.oData, t, context, setAlert, nextYear]);

    const handleCheck = useCallback(async (checked: boolean) => {
        const alertProps = await onCheck(checked);
        setAlert(alertProps);
    }, [onCheck, setAlert]);

    const columns: IColumn[] = useMemo(() => {
        return [{
            id: PrTaxpayerDeclarationEntity.StatusCode,
            label: t("PayrollYearOperation:TaxPayerDeclaration.TaxPayerDeclarationStatusLabel"),
        }, {
            id: "count",
            label: t("PayrollYearOperation:Detail.Count"),
        }];
    }, [t]);

    return (<>
        <AlertWrapper>{alert}</AlertWrapper>
        <DataGrid columns={1}>
            <DataGridCell key={"withoutDeclaration"}
                          $isBold>{t("PayrollYearOperation:TaxPayerDeclaration.WithoutDeclaration") + ":"}</DataGridCell>
            <DataGridCell key={"count"}>{
                employeesWithoutDeclaration.length === 0 ? 0 :
                    getTableDrillDownLink(employeesWithoutDeclaration.length.toString(), {
                        route: ROUTE_EMPLOYEES,
                        context: context,
                        filters: {
                            [PrEmployeeEntity.NumberOurs]: employeesWithoutDeclaration,
                        }
                    }).value
            }</DataGridCell>
        </DataGrid>
        {!!rows.length && <>
            <Text>{t("PayrollYearOperation:TaxPayerDeclaration.TaxPayerDeclarationStatus")}</Text>
            <TableWrapper>
                <TableWithAutoSizedColumns
                    rows={rows}
                    columns={columns}
                    disableSort={true}
                    disableVirtualization={true}/>
            </TableWrapper>
        </>}
        <ClickableStyled link={ROUTE_TAXPAYER_DECLARATIONS}
                         isLink>{t("PayrollYearOperation:TaxPayerDeclaration.GoToTaxPayerDeclaration")}</ClickableStyled>
        <SectionSwitch
                isChecked={task.IsChecked}
                onCheck={handleCheck}/>
    </>);
};

export default TaxPayerDeclaration;